import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { trackEvent } from "../../utils/analytics";
import { removeCommaFromNumString } from "../../utils/common";
import { CDVALET_BASE_URL, TRACKDEV_URL } from "../../utils/constant";
import { apiFetch } from "../ApiCall";
import Loader from "../Loader";
import {
  DesktopViewTable,
  FilterInputs,
  Footer,
  HeaderContent,
  MobileViewTable,
  PrivacyButton,
  sortData,
} from "./CDRatesComponent";

const CDRates = ({
  perPageRecord = 5,
  showPagination = true,
  headerLogo = true,
  footerLogo = true,
  depositAmount=0,
}) => {
  const formattedDepositAmount=depositAmount.toLocaleString('en-US');
  const PER_PAGE_RECORD = Number(perPageRecord) || 5;

  const [params, setParams] = useState({
    pageNumber: 1,
    numberOfRates: PER_PAGE_RECORD,
    investmentAmount:formattedDepositAmount ,
    partnersOnly: true,
    sort: "byApy",
    sortOrder: "DESC",
  });

  const [rateTableData, setRateTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCollapsibleRestriction, setIsCollapsibleRestriction] =
    useState(false);
  const [totalPages, setTotalPage] = useState(1);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [formData, setFormData] = useState({
    minTerms: "",
    maxTerms: "",
    investmentAmount: formattedDepositAmount,
  });
  const [selectedSort, setSelectedSort] = useState("APY (DSC)");
  const [investmentAmount, setInvestmentAmount] = useState(formattedDepositAmount);
  const [apyPercentage, setApyPercentage] = useState("0");
   const hasMounted = useRef(false);
  const affiliateId = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("affiliateId");
    } catch {
      return null;
    }
  }, []);

  async function getRateList(apiParams) {
    try {
      const rateList = await apiFetch(
        `/api/v1/rate/get/rates`,
        "POST",
        apiParams,
        { "content-type": "application/json" },
        TRACKDEV_URL
      );
      setIsLoading(false);
      setRateTableData(rateList?.data?.records || []);
      setTotalPage(rateList?.data?.totalCount || 0);
    } catch (e) {
      console.error("failed to get rateList:", e.message);
      setIsLoading(false);
    }
  }

  const apiDataCall = useCallback(() => {
    setIsLoading(true);
    const apiParams = {
      ...params,
      numberOfRates: PER_PAGE_RECORD,
      investmentAmount: params.investmentAmount
        ? removeCommaFromNumString(params.investmentAmount)
        : 0,
    };
    if (apiParams?.minTerms === 5) {
      apiParams.maxTerms = 5;
      delete apiParams.minTerms;
    }
    if (apiParams.maxTerms === 61) {
      delete apiParams.maxTerms;
      apiParams.minTerms = 61;
    }
    setCurrentPageIndex(apiParams.pageNumber);
    getRateList(apiParams);
  }, [params, PER_PAGE_RECORD]);

  const setPagination = (page, btnType) => {
    setCurrentPageIndex(page);
    const paramVal = { ...params, pageNumber: page };
    setParams(paramVal);
    trackEvent(`Affiliate_pagination_${btnType}_Action`, { ...paramVal });
  };

  const handleInvestmentAmountChange = (e) => {
    let value = e.target.value;

    let digitsOnly = value.replace(/\D/g, "");
    if (digitsOnly.length > 9) {
      return;
    }
    if (digitsOnly) {
      value = new Intl.NumberFormat("en-US").format(parseInt(digitsOnly, 10));
    } else {
      value = "";
    }
    trackEvent("Affiliate_Filter_Action", {
      ...params,
      investmentAmount: value,
      affiliateId,
    });
    setInvestmentAmount(value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const param = { ...params };
    const filterData = { ...formData };
    if (name === "minTerms") {
      if (value === "5") {
        filterData.maxTerms = "";
        delete param.maxTerms;
      }
      if (value === "") {
        delete filterData.minTerms;
        delete param.minTerms;
      }
    } else if (name === "maxTerms") {
      if (value === "61") {
        filterData.minTerms = "";
        delete param.minTerms;
      }
      if (value === "") {
        delete filterData.maxTerms;
        delete param.maxTerms;
      }
    }
    trackEvent("Affiliate_Filter_Action", {
      ...params,
      ...filterData,
      affiliateId,
      [name]: name === "investmentAmount" ? value : parseInt(value || 0, 10),
    });

    setFormData({
      ...filterData,
      [name]: name === "investmentAmount" ? value : parseInt(value || 0, 10),
    });

    setParams({
      ...param,
      [name]: name === "investmentAmount" ? value : parseInt(value || 0, 10),
      pageNumber: 1,
    });
  };

  const onSort = (sort, sortOrder) => {
    const paramsVal = {
      ...params,
      pageNumber: 1,
      sort,
      sortOrder,
    };
    onSortTrackEvent(paramsVal);
    setParams(paramsVal);
  };

  const handleSelect = (val) => {
    const item = sortData.find((s) => Object.keys(s)[0] === val);
    setSelectedSort(val);
    if (item) {
      const { sort, order } = item[val];
      const paramsVal = {
        ...params,
        pageNumber: 1,
        sort,
        sortOrder: order,
      };
      onSortTrackEvent(paramsVal);
      setParams(paramsVal);
    }
  };

  const logoClick = () => {
    trackEvent("Affiliate_Logo_Action", { affiliateId });
  };

  const onPrivacyClick = () => {
    trackEvent('Affiliate_Privacy_Action', { affiliateId });
  }

  const onSortTrackEvent = (paramsVal) => {
    trackEvent("Affiliate_sorting_Event_Action", {
      ...paramsVal,
      affiliateId,
    });
  };

  useEffect(() => {
    const h = setTimeout(() => {
      if (investmentAmount) {
        handleChange({
          target: { name: "investmentAmount", value: investmentAmount },
        });
      }
    }, 2000);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investmentAmount]);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    apiDataCall();
  }, [apiDataCall]);

  useEffect(() => {
    (async () => {
      try {
        const maxRate = await apiFetch(
          `/api/v1/rate/get/topApy`,
          "GET",
          "",
          { Origin: CDVALET_BASE_URL },
          TRACKDEV_URL
        );
        setApyPercentage(maxRate?.data?.apy);
      } catch (error) {
        console.error(`Failed to get top APY`, error.message);
      }
    })();
  }, []);

  const totalPageCount = Math.max(
    1,
    Math.ceil((totalPages || 0) / PER_PAGE_RECORD)
  );
  const allowPagination = showPagination && totalPages > 1;

  return (
    <Container fluid>
      <div className="main-container pt-10 sm:px-0 lg:px-6 bg-gray-100 min-h-screen main-section">
        <HeaderContent
          showLogo={headerLogo}
          apyPerecentage={apyPercentage}
          logoClick={logoClick}
        />
        <hr className="horizontal-line mt-4" />
        <div className="d-flex justify-content-end" ><PrivacyButton onPrivacyClick={onPrivacyClick} /></div>
        <FilterInputs
          disabled={isLoading}
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          handleSelect={handleSelect}
          selected={selectedSort}
          investmentAmount={investmentAmount}
          handleInvestmentAmountChange={handleInvestmentAmountChange}
        />
        <Loader loading={isLoading}>
          <section className="table-section p-6 mt-4 rounded-lg shadow-md overflow-x-auto">
            <DesktopViewTable
              isLoading={isLoading}
              rateTableData={rateTableData}
              isCollapsibleRestriction={isCollapsibleRestriction}
              setIsCollapsibleRestriction={setIsCollapsibleRestriction}
              onSort={onSort}
              selectedSort={params.sort}
              sortType={params.sortOrder}
              affiliateId={affiliateId}
            />
            <MobileViewTable
              isLoading={isLoading}
              rateTableData={rateTableData}
              isCollapsibleRestriction={isCollapsibleRestriction}
              setIsCollapsibleRestriction={setIsCollapsibleRestriction}
              affiliateId={affiliateId}
            />
            <Footer
              isShowPagination={allowPagination}
              currentIndex={currentPageIndex}
              totalPages={totalPageCount}
              setPagination={setPagination}
              isLoading={isLoading}
              showLogo={footerLogo}
              logoClick={logoClick}
            />
          </section>
        </Loader>
      </div>
    </Container>
  );
};

export default CDRates;
